import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../services/api';

export default function Cadastro() {
  const [users, setUsers] = useState([]);
  const [empresas, setEmpresas] = useState([]);

  const [nomeCompleto, setNomeCompleto] = useState('');
  const [usuario, setUsuario] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [tipo, setTipo] = useState('vendedor');
  const [loja, setLoja] = useState('');

  const [msg, setMsg] = useState({ texto: '', tipo: '' });
  const [editandoId, setEditandoId] = useState(null);

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [saving, setSaving] = useState(false);

  const empresasAtivas = useMemo(() => {
    return empresas.filter((emp) => emp.ativo !== false);
  }, [empresas]);

  const lojaSelecionada = useMemo(() => {
    return empresas.find((emp) => emp.codigoLoja === loja) || null;
  }, [empresas, loja]);

  const showMsg = useCallback((texto, tipo = 'sucesso') => {
    setMsg({ texto, tipo });
    setTimeout(() => setMsg({ texto: '', tipo: '' }), 4000);
  }, []);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await api.get('/users');
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      showMsg('Erro ao carregar usuários', 'erro');
    } finally {
      setLoadingUsers(false);
    }
  }, [showMsg]);

  const loadEmpresas = useCallback(async () => {
    setLoadingEmpresas(true);
    try {
      const response = await api.get('/empresas');
      const lista = Array.isArray(response.data) ? response.data : [];
      setEmpresas(lista);

      if (!editandoId && !loja) {
        const primeiraAtiva = lista.find((emp) => emp.ativo !== false) || lista[0];
        if (primeiraAtiva) {
          setLoja(primeiraAtiva.codigoLoja);
        }
      }
    } catch (error) {
      showMsg('Erro ao carregar empresas', 'erro');
    } finally {
      setLoadingEmpresas(false);
    }
  }, [showMsg, editandoId, loja]);

  useEffect(() => {
    loadUsers();
    loadEmpresas();
  }, [loadUsers, loadEmpresas]);

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    return parts.length > 1
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  };

  function limparFormulario() {
    setEditandoId(null);
    setNomeCompleto('');
    setUsuario('');
    setEmail('');
    setSenha('');
    setTipo('vendedor');

    const primeiraAtiva = empresasAtivas[0];
    setLoja(primeiraAtiva ? primeiraAtiva.codigoLoja : '');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);

    try {
      if (!nomeCompleto || !tipo || !loja || (!editandoId && (!usuario || !senha))) {
        showMsg('Preencha todos os campos obrigatórios.', 'erro');
        setSaving(false);
        return;
      }

      if (editandoId) {
        const payload = {
          nomeCompleto,
          usuario,
          email,
          tipo,
          loja
        };

        if (senha.trim()) {
          payload.senha = senha;
        }

        await api.put(`/users/${editandoId}`, payload);
        showMsg('Usuário atualizado com sucesso!');
      } else {
        await api.post('/users', {
          nomeCompleto,
          usuario,
          email,
          senha,
          tipo,
          loja
        });

        showMsg('Usuário criado com sucesso!');
      }

      limparFormulario();
      loadUsers();
    } catch (error) {
      showMsg(error.response?.data?.error || 'Erro ao processar solicitação', 'erro');
    } finally {
      setSaving(false);
    }
  }

  function editarUser(user) {
    setEditandoId(user._id);
    setNomeCompleto(user.nomeCompleto || '');
    setUsuario(user.usuario || '');
    setEmail(user.email || '');
    setTipo(user.tipo || 'vendedor');
    setLoja(user.loja || '');
    setSenha('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function toggleUserStatus(user) {
    const acao = user.ativo ? 'disable' : 'enable';

    if (!window.confirm(`Deseja realmente ${user.ativo ? 'desativar' : 'ativar'} o acesso de ${user.nomeCompleto}?`)) {
      return;
    }

    try {
      await api.put(`/users/${user._id}/${acao}`);
      loadUsers();
      showMsg(`Status de ${user.nomeCompleto} alterado!`);
    } catch (error) {
      showMsg('Erro ao alterar status', 'erro');
    }
  }

  async function deletarUser(id) {
    const confirmacao = window.prompt('⚠️ ZONA DE PERIGO ⚠️\nEssa ação é irreversível. Digite DELETAR para confirmar:');
    if (confirmacao !== 'DELETAR') return;

    try {
      await api.delete(`/users/${id}`);
      showMsg('Usuário removido do sistema.', 'aviso');
      loadUsers();
    } catch {
      showMsg('Erro ao deletar usuário', 'erro');
    }
  }

  return (
    <div style={styles.page}>
      <style>{`
        .input-focus:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
        .btn-hover:hover { transform: translateY(-1px); filter: brightness(110%); }
        .row-hover:hover { background-color: #f8fafc; }
        .table-action:hover { background-color: #e2e8f0; transform: scale(1.1); }
        @keyframes slideIn { from { transform: translateY(-10px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

        @media (max-width: 768px) {
          .cad-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 12px;
          }

          .cad-back-btn {
            width: 100%;
            justify-content: center;
          }

          .cad-form-row {
            flex-direction: column;
          }

          .cad-form-actions {
            flex-direction: column;
          }

          .cad-form-actions button {
            width: 100%;
          }
        }
      `}</style>

      <header style={styles.header}>
        <div style={styles.headerContent} className="cad-header">
          <div>
            <h1 style={styles.title}>Gestão de Time</h1>
            <p style={styles.subtitle}>Administre acessos e permissões por empresa/unidade</p>
          </div>

          <button
            style={styles.backBtn}
            onClick={() => (window.location.href = '/home')}
            className="btn-hover cad-back-btn"
          >
            <span>←</span> Voltar
          </button>
        </div>
      </header>

      <main style={styles.container}>
        {msg.texto && (
          <div
            style={{
              ...styles.toast,
              backgroundColor:
                msg.tipo === 'erro' ? '#fee2e2' : msg.tipo === 'aviso' ? '#ffedd5' : '#dcfce7',
              color:
                msg.tipo === 'erro' ? '#991b1b' : msg.tipo === 'aviso' ? '#9a3412' : '#166534',
              borderLeft: `4px solid ${
                msg.tipo === 'erro' ? '#ef4444' : msg.tipo === 'aviso' ? '#f97316' : '#22c55e'
              }`
            }}
          >
            {msg.texto}
          </div>
        )}

        <section style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.headerIcon}>{editandoId ? '✏️' : '✨'}</div>
            <div>
              <h3 style={styles.cardTitle}>
                {editandoId ? 'Editando Usuário' : 'Novo Colaborador'}
              </h3>
              <p style={styles.cardSubtitle}>
                {editandoId
                  ? 'Atualize os dados abaixo'
                  : 'Preencha as informações para criar um acesso'}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Nome Completo</label>
              <input
                style={styles.input}
                className="input-focus"
                placeholder="Ex: João Silva"
                value={nomeCompleto}
                onChange={(e) => setNomeCompleto(e.target.value)}
                required
              />
            </div>

            <div style={styles.rowGroup} className="cad-form-row">
              <div style={styles.inputGroup}>
                <label style={styles.label}>Login de Acesso</label>
                <input
                  style={styles.input}
                  className="input-focus"
                  placeholder="Ex: joao.vendas"
                  value={usuario}
                  onChange={(e) => setUsuario(e.target.value)}
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>E-mail Google</label>
                <input
                  type="email"
                  style={styles.input}
                  className="input-focus"
                  placeholder="Ex: cliente@gmail.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>
                {editandoId ? 'Nova Senha (opcional)' : 'Senha Temporária'}
              </label>
              <input
                type="password"
                style={styles.input}
                className="input-focus"
                placeholder={editandoId ? 'Deixe em branco para manter' : '••••••••'}
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required={!editandoId}
              />
            </div>

            <div style={styles.rowGroup} className="cad-form-row">
              <div style={styles.inputGroup}>
                <label style={styles.label}>Perfil</label>
                <select
                  style={styles.select}
                  className="input-focus"
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                >
                  <option value="vendedor">Vendedor</option>
                  <option value="admin">Administrador (Gerente)</option>
                </select>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Empresa / Unidade</label>
                <select
                  style={styles.select}
                  className="input-focus"
                  value={loja}
                  onChange={(e) => setLoja(e.target.value)}
                  disabled={loadingEmpresas}
                  required
                >
                  <option value="">
                    {loadingEmpresas ? 'Carregando empresas...' : 'Selecione a empresa'}
                  </option>

                  {empresasAtivas.map((empresa) => (
                    <option key={empresa._id} value={empresa.codigoLoja}>
                      {empresa.nome}
                      {empresa.unidade ? ` - ${empresa.unidade}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {lojaSelecionada && (
              <div style={styles.empresaInfoBox}>
                <div style={styles.empresaInfoTitle}>Empresa vinculada</div>

                <div style={styles.empresaInfoGrid}>
                  <div><strong>Nome:</strong> {lojaSelecionada.nome}</div>
                  <div><strong>Código:</strong> {lojaSelecionada.codigoLoja}</div>
                  <div><strong>Unidade:</strong> {lojaSelecionada.unidade || 'Não informada'}</div>
                  <div><strong>Status:</strong> {lojaSelecionada.ativo === false ? 'Inativa' : 'Ativa'}</div>
                </div>
              </div>
            )}

            <div style={styles.formActions} className="cad-form-actions">
              {editandoId && (
                <button
                  type="button"
                  style={styles.btnCancel}
                  onClick={limparFormulario}
                  className="btn-hover"
                >
                  Cancelar
                </button>
              )}

              <button type="submit" style={styles.btnPrimary} className="btn-hover" disabled={saving}>
                {saving ? 'Salvando...' : editandoId ? 'Salvar Alterações' : 'Criar Acesso'}
              </button>
            </div>
          </form>
        </section>

        <section style={styles.card}>
          <div style={{ padding: '24px 32px', borderBottom: '1px solid #e2e8f0' }}>
            <h3 style={styles.cardTitle}>
              Membros da Equipe <span style={styles.countBadge}>{users.length}</span>
            </h3>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Colaborador</th>
                  <th style={styles.th}>Função</th>
                  <th style={styles.th}>Empresa / Unidade</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th} align="right">Gerenciar</th>
                </tr>
              </thead>

              <tbody>
                {loadingUsers ? (
                  <tr>
                    <td style={styles.td} colSpan="5">Carregando usuários...</td>
                  </tr>
                ) : (
                  users.map((user) => {
                    const empresaNome =
                      user.empresaId?.nome
                        ? `${user.empresaId.nome}${user.empresaId?.unidade ? ` - ${user.empresaId.unidade}` : ''}`
                        : user.loja;

                    return (
                      <tr key={user._id} style={styles.tr} className="row-hover">
                        <td style={styles.td}>
                          <div style={styles.userCell}>
                            <div style={styles.avatar}>
                              {getInitials(user.nomeCompleto)}
                            </div>

                            <div>
                              <div style={styles.userName}>{user.nomeCompleto}</div>
                              <div style={styles.userLogin}>@{user.usuario}</div>

                              {user.email && (
                                <div style={styles.userEmail}>{user.email}</div>
                              )}
                            </div>
                          </div>
                        </td>

                        <td style={styles.td}>
                          <span style={user.tipo === 'admin' ? styles.badgeAdmin : styles.badgeUser}>
                            {user.tipo === 'admin' ? '🛡️ Admin' : '💼 Vendedor'}
                          </span>
                        </td>

                        <td style={styles.td}>
                          <div style={{ fontSize: '13px', color: '#64748b' }}>
                            <div>{empresaNome || '--'}</div>
                            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                              {user.loja}
                            </div>
                          </div>
                        </td>

                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: user.ativo ? '#22c55e' : '#cbd5e1'
                              }}
                            />

                            <span
                              style={{
                                fontSize: '13px',
                                color: user.ativo ? '#15803d' : '#94a3b8',
                                fontWeight: 500
                              }}
                            >
                              {user.ativo ? 'Ativo' : 'Desativado'}
                            </span>
                          </div>
                        </td>

                        <td style={styles.td} align="right">
                          <div style={styles.actionButtons}>
                            <button
                              style={styles.actionBtn}
                              onClick={() => editarUser(user)}
                              title="Editar"
                              className="table-action"
                            >
                              ✏️
                            </button>

                            <button
                              style={{
                                ...styles.actionBtn,
                                color: user.ativo ? '#f59e0b' : '#10b981'
                              }}
                              onClick={() => toggleUserStatus(user)}
                              title={user.ativo ? 'Desativar' : 'Ativar'}
                              className="table-action"
                            >
                              {user.ativo ? '⏸️' : '▶️'}
                            </button>

                            <button
                              style={{ ...styles.actionBtn, color: '#ef4444' }}
                              onClick={() => deletarUser(user._id)}
                              title="Excluir"
                              className="table-action"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f1f5f9',
    fontFamily: '"Inter", sans-serif',
    color: '#334155'
  },

  header: {
    background: 'rgba(15, 23, 42, 0.95)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
  },

  headerContent: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 700,
    color: '#fff'
  },

  subtitle: {
    margin: 0,
    color: '#94a3b8',
    fontSize: '13px'
  },

  backBtn: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.05)',
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  container: {
    maxWidth: '1000px',
    margin: '40px auto',
    padding: '0 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '32px'
  },

  toast: {
    padding: '12px 20px',
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '14px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
    animation: 'slideIn 0.3s ease-out',
    marginBottom: '-10px'
  },

  card: {
    background: '#fff',
    borderRadius: '16px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden'
  },

  cardHeader: {
    padding: '32px 32px 0 32px',
    display: 'flex',
    gap: '16px',
    marginBottom: '24px'
  },

  headerIcon: {
    fontSize: '24px',
    background: '#f8fafc',
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #e2e8f0'
  },

  cardTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 700,
    color: '#1e293b'
  },

  cardSubtitle: {
    margin: '4px 0 0',
    fontSize: '14px',
    color: '#64748b'
  },

  countBadge: {
    background: '#f1f5f9',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    color: '#64748b',
    marginLeft: '8px'
  },

  form: {
    padding: '0 32px 32px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },

  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: 1
  },

  rowGroup: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap'
  },

  label: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },

  input: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.2s',
    width: '100%',
    boxSizing: 'border-box'
  },

  select: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    background: '#fff',
    fontSize: '14px',
    cursor: 'pointer',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box'
  },

  empresaInfoBox: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '14px 16px'
  },

  empresaInfoTitle: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#334155',
    marginBottom: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },

  empresaInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '8px',
    fontSize: '13px',
    color: '#64748b'
  },

  formActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '12px',
    justifyContent: 'flex-end'
  },

  btnPrimary: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    background: '#3b82f6',
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '14px'
  },

  btnCancel: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    background: '#fff',
    color: '#64748b',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '14px'
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '700px'
  },

  th: {
    padding: '16px 24px',
    background: '#f8fafc',
    textAlign: 'left',
    fontSize: '11px',
    color: '#64748b',
    textTransform: 'uppercase',
    fontWeight: 700,
    borderBottom: '1px solid #e2e8f0'
  },

  tr: {
    borderBottom: '1px solid #f1f5f9',
    transition: 'background 0.2s'
  },

  td: {
    padding: '16px 24px',
    fontSize: '14px',
    color: '#334155',
    verticalAlign: 'middle'
  },

  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },

  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: '#e0f2fe',
    color: '#0369a1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 700,
    border: '1px solid #bae6fd'
  },

  userName: {
    fontWeight: 600,
    color: '#1e293b'
  },

  userLogin: {
    fontSize: '12px',
    color: '#64748b'
  },

  userEmail: {
    fontSize: '11px',
    color: '#94a3b8',
    marginTop: '2px'
  },

  badgeAdmin: {
    padding: '4px 10px',
    borderRadius: '20px',
    background: '#f5f3ff',
    color: '#7c3aed',
    fontSize: '11px',
    fontWeight: 700,
    border: '1px solid #ddd6fe'
  },

  badgeUser: {
    padding: '4px 10px',
    borderRadius: '20px',
    background: '#eff6ff',
    color: '#2563eb',
    fontSize: '11px',
    fontWeight: 700,
    border: '1px solid #bfdbfe'
  },

  actionButtons: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end'
  },

  actionBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    fontSize: '16px'
  }
};